import { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { Check, Copy, Eye, EyeOff } from 'lucide-react';

import { trackSegmentEvent } from 'app/utils';
import { SOURCE, ACTION } from 'app/analytics';

import { Button } from 'components/ui/button';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';

import { cn } from 'lib/utils';

import { ryeSyntaxTheme } from './syntaxTheme';

interface CodeBlockProps {
  code: string;
  language?: string;
  // When true, drops the outer border so the block can sit edge-to-edge
  // inside an existing surface (e.g. an attached card footer).
  flush?: boolean;
  // Optional control rendered at the left side of a header bar (e.g. a
  // language selector). When provided, the copy button moves out of its
  // overlay position and into the same header bar to keep both controls on
  // a shared baseline.
  headerLeft?: React.ReactNode;
  // When provided, the block renders `mask(code)` by default and a
  // visibility toggle is shown. The copy button always copies the
  // unredacted `code`.
  mask?: (code: string) => string;
}

export const CodeBlock = ({
  code,
  language = 'typescript',
  flush = false,
  headerLeft,
  mask,
}: CodeBlockProps) => {
  const { copy, didCopy } = useCopyToClipboard();
  const [revealed, setRevealed] = useState(false);

  const handleCopy = () => {
    copy(code);
    trackSegmentEvent(
      SOURCE.CODE_EXAMPLE_MODULE,
      ACTION.CLICK,
      'copy_quick_start_code_example_button',
    );
  };

  // Only enable the toggle if the masker actually changes the code — otherwise
  // we'd show a Show/Hide control over text that has nothing to hide (e.g. a
  // snippet built with a placeholder key).
  const maskedCode = mask?.(code);
  const isMaskable = maskedCode !== undefined && maskedCode !== code;
  const displayed = isMaskable && !revealed ? maskedCode : code;
  const toggleTitle = revealed ? 'Hide secret' : 'Show secret';

  const toggleButton = isMaskable && (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => setRevealed((r) => !r)}
      title={toggleTitle}
      aria-pressed={revealed}
    >
      {revealed ? (
        <>
          <EyeOff className="h-3.5 w-3.5" />
          Hide
        </>
      ) : (
        <>
          <Eye className="h-3.5 w-3.5" />
          Show
        </>
      )}
    </Button>
  );

  const copyButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleCopy}
      title="Copy code"
      className={cn(!headerLeft && !isMaskable && 'absolute right-3 top-3')}
    >
      {didCopy ? (
        <>
          <Check className="h-3.5 w-3.5" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          Copy
        </>
      )}
    </Button>
  );

  // When the block needs a toggle but the caller didn't supply a header,
  // synthesise an overlay row in the corner so both buttons share a baseline.
  const overlayControls = !headerLeft && isMaskable && (
    <div className="absolute right-3 top-3 flex items-center gap-2">
      {toggleButton}
      {copyButton}
    </div>
  );

  return (
    <div
      className={cn(
        'relative block w-full overflow-hidden bg-[var(--surface-2)]',
        !flush && 'border border-[var(--line)]',
      )}
    >
      {headerLeft && (
        <div className="grid grid-cols-[1fr_auto] items-center gap-2 border-b border-[var(--line)] px-3 py-2">
          {headerLeft}
          <div className="flex items-center gap-2">
            {toggleButton}
            {copyButton}
          </div>
        </div>
      )}
      <SyntaxHighlighter
        language={language}
        style={ryeSyntaxTheme}
        showLineNumbers
        wrapLongLines={false}
        lineNumberStyle={{ color: 'var(--syntax-dim)', minWidth: '2.25em' }}
      >
        {displayed}
      </SyntaxHighlighter>
      {!headerLeft && !isMaskable && copyButton}
      {overlayControls}
    </div>
  );
};
